import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Workflow } from '../../store/project/projectSlice';
import {
  WindowRun, fetchRunsInWindow, dagIdOf, isActiveState, stateLabel, fmtTime, fmtDuration,
} from '../../services/executionApi';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../utils/formatters';

interface RunTimelineProps {
  workflows: Workflow[];
  /** 실행이 끝나면 이 값이 바뀌어 다시 읽는다 */
  reloadKey?: number;
  onSelectWorkflow?: (w: Workflow) => void;
  /** 몇 개까지 보여 줄지 */
  top?: number;
  /** 읽은 구간·실행을 그대로 넘긴다 — 아래 실행 이력 목록이 그래프와 같은 데이터를 쓰도록 (useCallback 으로 넘길 것) */
  onWindowData?: (d: { runs: WindowRun[]; range: { start: Date; end: Date } | null; loading: boolean }) => void;
}

type Preset = '30m' | '1h' | '3h' | '6h' | '24h' | 'custom';
// 표시 이름은 번역 키 execution:tl.last<key> — 모듈 상수라 렌더에서 t() 로 바꾼다
const PRESETS: Array<{ key: Preset; ms: number }> = [
  { key: '30m', ms: 30 * 60 * 1000 },
  { key: '1h', ms: 60 * 60 * 1000 },
  { key: '3h', ms: 3 * 60 * 60 * 1000 },
  { key: '6h', ms: 6 * 60 * 60 * 1000 },
  { key: '24h', ms: 24 * 60 * 60 * 1000 },
];
const AUTO_REFRESH_MS = 30 * 1000;

const BAR_STYLE: Record<string, string> = {
  success: 'bg-green-400',
  failed: 'bg-red-400',
  running: 'bg-blue-400 animate-pulse',
  queued: 'bg-gray-300',
  upstream_failed: 'bg-red-300',
};

/** datetime-local 입력값 ↔ Date (로컬 시각) */
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (s: string): Date | null => { const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d; };

/** 눈금 간격 — 구간 길이에 따라 5분~4시간 */
function tickStepMs(spanMs: number): number {
  const m = 60 * 1000;
  if (spanMs <= 30 * m) return 5 * m;
  if (spanMs <= 60 * m) return 10 * m;
  if (spanMs <= 3 * 60 * m) return 30 * m;
  if (spanMs <= 6 * 60 * m) return 60 * m;
  return 4 * 60 * m;
}

function fmtMs(ms: number): string {
  return formatDuration(Math.round(ms / 1000));
}

interface Segment { run: WindowRun; s: number; e: number; clippedS: number; clippedE: number }
interface Row { workflow: Workflow; overlapMs: number; segments: Segment[] }

/**
 * 구간 안에서 오래 돈 워크플로 타임라인.
 *
 * 순위는 **구간과 겹친 시간**으로 매긴다 — 전체 소요가 아니라. 한 시간짜리 실행이라도 구간에 5분만
 * 걸쳐 있으면, 30분짜리 실행이 20분 걸쳐 있는 것보다 뒤다. 끝나지 않은 실행은 '지금'까지로 센다.
 * 조회 전용이다.
 */
const RunTimeline: React.FC<RunTimelineProps> = ({ workflows, reloadKey, onSelectWorkflow, top = 5, onWindowData }) => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<Preset>('1h');
  const [customStart, setCustomStart] = useState<string>(() => toLocalInput(new Date(Date.now() - 60 * 60 * 1000)));
  const [customEnd, setCustomEnd] = useState<string>(() => toLocalInput(new Date()));
  const [runs, setRuns] = useState<WindowRun[]>([]);
  const [now, setNow] = useState<Date>(new Date());
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 읽은 결과를 부모에게 그대로 넘긴다 — 아래 '이 구간의 실행 이력' 이 같은 데이터를 써야 그래프와 어긋나지 않는다
  useEffect(() => { onWindowData?.({ runs, range, loading }); }, [runs, range, loading, onWindowData]);

  const ids = useMemo(() => workflows.map((w) => w.id), [workflows]);
  const byDag = useMemo(() => new Map(workflows.map((w) => [dagIdOf(w.id), w])), [workflows]);

  /** 지금 시점 기준의 구간 — 프리셋은 상대, 직접 지정은 입력값 그대로 */
  const resolveWindow = useCallback((): { start: Date; end: Date } | null => {
    if (preset === 'custom') {
      const s = fromLocalInput(customStart); const e = fromLocalInput(customEnd);
      return s && e && e > s ? { start: s, end: e } : null;
    }
    const p = PRESETS.find((x) => x.key === preset)!;
    const end = new Date();
    return { start: new Date(end.getTime() - p.ms), end };
  }, [preset, customStart, customEnd]);

  const load = useCallback(async () => {
    const win = resolveWindow();
    if (!win) { setError(t('execution:tl.badRange')); return; }
    if (!ids.length) { setRuns([]); setRange(win); return; }
    setLoading(true); setError('');
    try {
      const r = await fetchRunsInWindow(ids, win.start, win.end);
      setRuns(r.runs); setNow(r.now); setRange(win);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [ids, resolveWindow, t]);

  useEffect(() => { load(); }, [load, reloadKey]);

  // 상대 구간('최근 …')은 시간이 흐르면 구간도 흐른다 — 주기적으로 다시 읽는다
  useEffect(() => {
    if (preset === 'custom') return undefined;
    const timer = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [preset, load]);

  const rows: Row[] = useMemo(() => {
    if (!range) return [];
    const S = range.start.getTime(); const E = range.end.getTime(); const N = now.getTime();
    const acc = new Map<string, Row>();
    for (const r of runs) {
      const w = byDag.get(r.dag_id);
      if (!w || !r.start_date) continue;
      const s = new Date(r.start_date).getTime();
      const e = r.end_date ? new Date(r.end_date).getTime() : Math.max(N, s);   // 끝나지 않았으면 지금까지
      const cs = Math.max(s, S); const ce = Math.min(e, E);
      if (ce <= cs) continue;
      const row = acc.get(w.id) || { workflow: w, overlapMs: 0, segments: [] };
      row.overlapMs += ce - cs;
      row.segments.push({ run: r, s, e, clippedS: cs, clippedE: ce });
      acc.set(w.id, row);
    }
    return Array.from(acc.values()).sort((a, b) => b.overlapMs - a.overlapMs).slice(0, top);
  }, [runs, range, now, byDag, top]);

  const span = range ? range.end.getTime() - range.start.getTime() : 1;
  const pct = (ms: number) => `${Math.max(0, Math.min(100, ((ms - (range?.start.getTime() || 0)) / span) * 100))}%`;

  const ticks = useMemo(() => {
    if (!range) return [];
    const step = tickStepMs(span);
    const first = Math.ceil(range.start.getTime() / step) * step;
    const out: number[] = [];
    for (let ms = first; ms <= range.end.getTime(); ms += step) out.push(ms);
    return out;
  }, [range, span]);

  const nowMs = now.getTime();
  const showNow = range && nowMs >= range.start.getTime() && nowMs <= range.end.getTime();
  const hhmm = (ms: number) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
        <span className="font-medium">{t('execution:tl.title', { n: top })}</span>
        <span className="text-xs text-gray-500">{t('execution:tl.rankBy')} <b>{t('execution:tl.rankByBold')}</b>{t('execution:tl.rankByTail')}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-2 py-0.5 text-xs rounded border ${preset === p.key ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              {t(`execution:tl.last${p.key}`)}
            </button>
          ))}
          <span className="text-xs text-gray-400 mx-1">|</span>
          <input
            type="datetime-local"
            value={preset === 'custom' ? customStart : (range ? toLocalInput(range.start) : customStart)}
            onChange={(e) => { setCustomStart(e.target.value); setPreset('custom'); }}
            className="border border-gray-300 rounded px-1 py-0.5 text-xs"
            title={t('execution:tl.rangeStart')}
          />
          <span className="text-xs text-gray-400">~</span>
          <input
            type="datetime-local"
            value={preset === 'custom' ? customEnd : (range ? toLocalInput(range.end) : customEnd)}
            onChange={(e) => { setCustomEnd(e.target.value); setPreset('custom'); }}
            className="border border-gray-300 rounded px-1 py-0.5 text-xs"
            title={t('execution:tl.rangeEnd')}
          />
          <button
            className="px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            onClick={load} disabled={loading}
          >
            {loading ? t('execution:tl.reading') : t('execution:tl.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="px-4 py-2 text-xs text-red-700 bg-red-50">{t('execution:tl.loadFailed', { message: error })}</div>}

      {/* 그래프 영역은 높이를 고정한다 — 구간에 실행이 없거나 읽는 중이면 내용이 사라지는데,
          그때마다 영역이 접히면 아래 목록이 위아래로 튄다.
          자리는 가장 많이 나올 때(행 top 개 h-8 = 2rem) 기준으로 잡는다: 행 + 시간축(1.25rem) + 범례·여백(1.5rem) + 패딩(1.5rem).
          안내 문구는 flex-1 로 남는 자리를 채워, 실행이 있든 없든 카드 높이가 같다. */}
      <div className="p-3 flex flex-col" style={{ minHeight: `calc(${top} * 2rem + 4.25rem)` }}>
        {/* 행 영역 — 실행이 몇 개든 top 개 자리를 차지한다. 눈금선·'지금' 선은 이 영역 전체에 한 번만 그린다
            (예전에는 행마다 그려서, 행 수가 줄면 격자도 함께 사라졌다) */}
        <div className="flex" style={{ height: `calc(${top} * 2rem)` }}>
          <div className="w-56 shrink-0 pr-2">
            {rows.map((row) => (
              <div key={row.workflow.id} className="h-8 flex items-center">
                <button
                  type="button"
                  className="truncate text-left text-sm text-gray-800 hover:text-blue-700"
                  title={t('execution:tl.inRange', { name: row.workflow.name, duration: fmtMs(row.overlapMs) })}
                  onClick={() => onSelectWorkflow?.(row.workflow)}
                >
                  <span className="mr-1">{row.workflow.icon || '📋'}</span>{row.workflow.name}
                </button>
              </div>
            ))}
          </div>

          <div className="flex-1 min-w-0 relative">
            {ticks.map((ms) => <div key={ms} className="absolute top-0 bottom-0 border-l border-gray-100" style={{ left: pct(ms) }} />)}
            {showNow && <div className="absolute top-0 bottom-0 border-l border-dashed border-blue-400" style={{ left: pct(nowMs) }} />}

            {rows.map((row) => (
              <div key={row.workflow.id} className="h-8 relative border-b border-gray-50">
                {row.segments.map((seg) => {
                  const left = pct(seg.clippedS);
                  const width = `${Math.max(0.4, ((seg.clippedE - seg.clippedS) / span) * 100)}%`;
                  const active = isActiveState(seg.run.state);
                  return (
                    <div
                      key={seg.run.dag_run_id}
                      className={`absolute top-1.5 h-5 rounded ${BAR_STYLE[seg.run.state || ''] || 'bg-gray-300'} ${active ? 'ring-1 ring-blue-500' : ''}`}
                      style={{ left, width }}
                      title={t('execution:tl.segment', { state: stateLabel(seg.run.state), start: fmtTime(seg.run.start_date), end: seg.run.end_date ? fmtTime(seg.run.end_date) : t('execution:tl.inProgress'), duration: seg.run.end_date ? fmtDuration(seg.run.start_date, seg.run.end_date) : fmtMs(seg.e - seg.s), inRange: fmtMs(seg.clippedE - seg.clippedS) })}
                    />
                  );
                })}
                <div className="absolute right-1 top-0 h-8 flex items-center text-[0.625rem] text-gray-500 bg-white/80 px-1">
                  {fmtMs(row.overlapMs)}
                </div>
              </div>
            ))}

            {rows.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                {loading ? t('execution:tl.reading')
                  : error ? ''
                    : range ? t('execution:tl.noRuns', { start: fmtTime(range.start.toISOString()).slice(5, 16), end: fmtTime(range.end.toISOString()).slice(5, 16) })
                      : ''}
              </div>
            )}
          </div>
        </div>

        {/* 시간 축 — 행이 몇 개든 그래프 영역 맨 아래 같은 자리에 있다 */}
        <div className="flex">
          <div className="w-56 shrink-0 pr-2" />
          <div className="flex-1 min-w-0 h-5 relative text-[0.625rem] text-gray-500">
            {ticks.map((ms) => (
              <div key={ms} className="absolute -translate-x-1/2" style={{ left: pct(ms) }}>{hhmm(ms)}</div>
            ))}
            {showNow && <div className="absolute -translate-x-1/2 text-blue-600" style={{ left: pct(nowMs), top: 10 }}>{t('execution:tl.now')}</div>}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-3 text-[0.6875rem] text-gray-500">
          <span><span className="inline-block w-3 h-2 rounded bg-green-400 align-middle mr-1" />{t('execution:tl.legendSuccess')}</span>
          <span><span className="inline-block w-3 h-2 rounded bg-red-400 align-middle mr-1" />{t('execution:tl.legendFailed')}</span>
          <span><span className="inline-block w-3 h-2 rounded bg-blue-400 align-middle mr-1" />{t('execution:tl.legendRunning')}</span>
          <span><span className="inline-block w-3 h-2 rounded bg-gray-300 align-middle mr-1" />{t('execution:tl.legendOther')}</span>
          {preset !== 'custom' && <span className="text-gray-400">{t('execution:tl.autoRefresh')}</span>}
        </div>
      </div>
    </div>
  );
};

export default RunTimeline;
